// ============================================
// CALENDAR i18n HELPERS
// ============================================
//
// t16: pure helpers that resolve display labels for the Calendar view's
// config-driven UI (dateSource, viewMode, colorBy). Mirrors
// kanban-i18n.ts but stays Calendar-only.

import type { CustomField } from '@/lib/schemas';

export type TranslateFn = (key: any) => string;

// Translate the active dateSource into a user-facing label. Sprint
// sources are only meaningful when the project actually has a Sprint
// field — the Settings panel disables them otherwise, but we still
// return a label here so the dropdown can render them.
export function getDateSourceLabel(
    source: string,
    t: TranslateFn
): string {
    return t(`calendar.dateSource.${source}`);
}

// Translate the active viewMode (month/week/agenda) into a label.
export function getViewModeLabel(
    mode: string,
    t: TranslateFn
): string {
    return t(`calendar.viewMode.${mode}`);
}

// Translate the active colorBy rule. Built-in rules ('status',
// 'priority', 'assignee') get fixed i18n labels; anything else is
// treated as a custom-field id and looked up against the project's
// customFields list so users see their custom field names, not raw
// ids (mirrors getFieldLabel in kanban-i18n.ts).
export function getColorByLabel(
    colorBy: string,
    customFields: CustomField[] | undefined,
    t: TranslateFn
): string {
    if (colorBy === 'status') return t('calendar.colorBy.status');
    if (colorBy === 'priority') return t('calendar.colorBy.priority');
    if (colorBy === 'assignee') return t('calendar.colorBy.assignee');

    if (customFields && customFields.length > 0) {
        const cf = customFields.find((c) => c.id === colorBy);
        if (cf) return cf.name;
    }
    return colorBy;
}

// Build the ordered list of selectable colorBy options for the
// Settings panel. Built-in rules first, then any SELECT / MULTI_SELECT
// / SPRINT custom fields the project has.
export function getColorByOptions(
    customFields: CustomField[] | undefined
): Array<{ value: string; label: string }> {
    const out: Array<{ value: string; label: string }> = [
        { value: 'status', label: 'calendar.colorBy.status' },
        { value: 'priority', label: 'calendar.colorBy.priority' },
        { value: 'assignee', label: 'calendar.colorBy.assignee' },
    ];
    if (customFields) {
        for (const cf of customFields) {
            if (cf.type === 'SELECT' || cf.type === 'MULTI_SELECT' || cf.type === 'SPRINT') {
                out.push({ value: cf.id, label: cf.name });
            }
        }
    }
    return out;
}
